import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import ImageUpload from '../../components/admin/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const emptyProject = {
    name: '',
    year: new Date().getFullYear(),
    status: 'completed',
    category: 'fishing',
    type: '',
    description_en: '',
    description_tr: '',
    image: '',
    order: 0
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (editingProject.id) {
        await axios.put(`${BACKEND_URL}/api/admin/projects/${editingProject.id}`, editingProject, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proje güncellendi');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/projects`, editingProject, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proje eklendi');
      }
      setEditingProject(null);
      setShowForm(false);
      fetchProjects();
    } catch (error) {
      toast.error('Hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu projeyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proje silindi');
      fetchProjects();
    } catch (error) {
      toast.error('Silme hatası');
    }
  };

  if (loading) return <div className="admin-loading">Yükleniyor...</div>;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Projeler Yönetimi</h1>
        <button
          onClick={() => {
            setEditingProject(emptyProject);
            setShowForm(true);
          }}
          className="btn-primary"
        >
          <Plus size={20} />
          Yeni Proje Ekle
        </button>
      </div>

      {showForm && (
        <div className="admin-modal">
          <div className="admin-modal-content large">
            <div className="admin-modal-header">
              <h2>{editingProject.id ? 'Projeyi Düzenle' : 'Yeni Proje Ekle'}</h2>
              <button onClick={() => setShowForm(false)} className="modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Proje Adı</label>
                  <input
                    type="text"
                    value={editingProject.name}
                    onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                    className="form-input"
                    placeholder="NB-1234 Vessel Name"
                  />
                </div>
                <div className="form-group">
                  <label>Yıl</label>
                  <input
                    type="number"
                    value={editingProject.year || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, year: e.target.value ? parseInt(e.target.value) : null })}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Durum</label>
                  <select
                    value={editingProject.status}
                    onChange={(e) => setEditingProject({ ...editingProject, status: e.target.value })}
                    className="form-select"
                  >
                    <option value="completed">Tamamlandı</option>
                    <option value="ongoing">Devam Ediyor</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Kategori</label>
                  <select
                    value={editingProject.category}
                    onChange={(e) => setEditingProject({ ...editingProject, category: e.target.value })}
                    className="form-select"
                  >
                    <option value="fishing">Balıkçı Gemileri</option>
                    <option value="ferry">Feribot</option>
                    <option value="offshore">Offshore</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Tip (Opsiyonel)</label>
                <input
                  type="text"
                  value={editingProject.type || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, type: e.target.value })}
                  className="form-input"
                  placeholder="Wind Turbine Support Vessel"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Açıklama (EN)</label>
                  <textarea
                    value={editingProject.description_en}
                    onChange={(e) => setEditingProject({ ...editingProject, description_en: e.target.value })}
                    rows="4"
                    className="form-textarea"
                  />
                </div>
                <div className="form-group">
                  <label>Açıklama (TR)</label>
                  <textarea
                    value={editingProject.description_tr}
                    onChange={(e) => setEditingProject({ ...editingProject, description_tr: e.target.value })}
                    rows="4"
                    className="form-textarea"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Proje Görseli</label>
                <ImageUpload
                  currentImage={editingProject.image}
                  onImageUploaded={(url) => setEditingProject({ ...editingProject, image: url })}
                />
              </div>

              <div className="form-group">
                <label>Sıra</label>
                <input
                  type="number"
                  value={editingProject.order}
                  onChange={(e) => setEditingProject({ ...editingProject, order: parseInt(e.target.value) })}
                  className="form-input"
                />
              </div>

              <button onClick={handleSave} className="btn-primary">
                <Save size={20} />
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Sıra</th>
              <th>Proje Adı</th>
              <th>Kategori</th>
              <th>Yıl</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project.id}>
                <td>{project.order}</td>
                <td>{project.name}</td>
                <td>{project.category}</td>
                <td>{project.year || '-'}</td>
                <td>
                  <span className={`status-badge ${project.status}`}>
                    {project.status === 'completed' ? 'Tamamlandı' : 'Devam Ediyor'}
                  </span>
                </td>
                <td>
                  <div className="table-actions">
                    <button
                      onClick={() => {
                        setEditingProject(project);
                        setShowForm(true);
                      }}
                      className="btn-icon btn-edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(project.id)}
                      className="btn-icon btn-delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProjects;
